/** Чистые функции «Аналитики ОПТ»: период по месяцам и сравнение, состояние адреса (вкладка, дерево,
 *  карточка), тексты ячеек с null, KPI с Δ, портфель заказов, покрытие себестоимостью, «что не
 *  вошло». Без React.
 *
 *  Правило null (контракт dwh wholesale v1.2): строка без себестоимости / суммы заказа маржи не
 *  имеет, в итогах её НЕ считаем нулём — сумма по оценённым строкам и пометка «часть без цены». */
import type {
  Agg,
  Bucket,
  ExcludedReason,
  Kind,
  LinesQuery,
  OutsideAgg,
  PerKind,
  ProductRow,
  TreeDim,
  TreeNode,
  TreeSortDir,
  TreeSortKey,
  WholesaleState,
  WholesaleSummary,
} from "@/api/wholesale";
import { readHierarchy, type Hierarchy, type HierarchyPreset } from "@/lib/hierarchy";
import { compact, fmtDate, int, monthLabel, NBSP, pct, rub } from "../ozon-analytics/format";
import { plural } from "../ozon-analytics/cogs";

export const KIND_LABEL: Record<Kind, string> = {
  shipped: "Отгружено",
  planned: "К отгрузке",
  overdue: "Просрочено",
};

// ─────────────── период ───────────────
export type PresetId = "tm" | "pm" | "tq" | "pq" | "ytd" | "all" | "custom";
export const PRESETS: { id: PresetId; label: string }[] = [
  { id: "tm", label: "Этот месяц" },
  { id: "pm", label: "Прошлый месяц" },
  { id: "tq", label: "Этот квартал" },
  { id: "pq", label: "Прошлый квартал" },
  { id: "ytd", label: "С начала года" },
  { id: "all", label: "Весь период" },
  { id: "custom", label: "Свой" },
];
/** Умолчание — «Этот месяц» (решение владельца 22.10.2026; было «С начала года»). */
export const DEFAULT_PRESET: PresetId = "tm";

export interface MonthRange {
  from: string; // YYYY-MM
  to: string;
}

const pad = (n: number): string => String(n).padStart(2, "0");
const idx = (m: string): number => Number(m.slice(0, 4)) * 12 + Number(m.slice(5, 7)) - 1;
const ofIdx = (i: number): string => `${Math.floor(i / 12)}-${pad((i % 12) + 1)}`;

export function isMonth(s: string | null | undefined): s is string {
  if (!s || !/^\d{4}-\d{2}$/.test(s)) return false;
  const m = Number(s.slice(5, 7));
  return m >= 1 && m <= 12;
}

export function monthOf(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
}

export function addMonths(m: string, k: number): string {
  return ofIdx(idx(m) + k);
}

export function monthsCount(r: MonthRange): number {
  return idx(r.to) - idx(r.from) + 1;
}

// ─────────────── сравнение ───────────────
/** Сравнение: с предыдущим периодом той же длины (по умолчанию) или без. История витрины —
 *  с 01.01.2026, поэтому «год назад» не предлагается. */
export type CmpMode = "prev" | "off";
export const CMP_MODES: { id: CmpMode; label: string }[] = [
  { id: "prev", label: "С прошлым периодом" },
  { id: "off", label: "Без сравнения" },
];
export const DEFAULT_CMP: CmpMode = "prev";

/** Предыдущий период той же длины в месяцах: октябрь → сентябрь, июль–сентябрь → апрель–июнь. */
export function prevRange(r: MonthRange): MonthRange {
  const n = monthsCount(r);
  return { from: addMonths(r.from, -n), to: addMonths(r.from, -1) };
}

/** Сравнение вне данных витрины (отгрузки — `meta.kinds.shipped`): Δ по неполным данным вводит
 *  в заблуждение — текст предупреждения, иначе null. Диапазон неизвестен (meta не пришла) — тоже
 *  предупреждение, а не тишина. */
export function cmpOutOfData(
  cmp: MonthRange | null,
  shipped: { month_min: string; month_max: string } | null | undefined,
): string | null {
  if (!cmp) return null;
  if (shipped === undefined) return "диапазон данных витрины неизвестен (нет ответа /meta)";
  if (shipped === null) return "отгрузок в витрине нет";
  const lo = shipped.month_min;
  const hi = shipped.month_max;
  if (cmp.to < lo) return `период сравнения целиком раньше начала данных (с ${monthLabel(lo)})`;
  if (cmp.from > hi) return `период сравнения целиком позже конца данных (по ${monthLabel(hi)})`;
  if (cmp.from < lo) return `период сравнения частично раньше начала данных (с ${monthLabel(lo)})`;
  if (cmp.to > hi) return `период сравнения частично позже конца данных (по ${monthLabel(hi)})`;
  return null;
}

/** Полнота периода против загруженных отгрузок 1С (`sales_date_to`): период частично или целиком
 *  позже последней загруженной отгрузки (1-е число месяца, запаздывающая загрузка 1С, свой период /
 *  «Весь период» в будущие месяцы заказов) — отгрузки там неполные; последний месяц неполный, а
 *  сравнение — полные месяцы — Δ занижена. Текст пометки или null. Дата неизвестна при сравнении —
 *  тоже пометка (полнота не проверена), а не тишина. */
export function incompleteMonthNote(r: MonthRange, cmp: MonthRange | null, salesDateTo: string | null): string | null {
  if (!salesDateTo) {
    return cmp ? "дата последней загруженной отгрузки 1С неизвестна — полнота периода не проверена" : null;
  }
  const last = salesDateTo.slice(0, 7);
  const upTo = `отгрузки 1С по ${fmtDate(salesDateTo)}`;
  const dz = cmp ? ": Δ занижена" : "";
  if (r.from > last) return `период целиком позже загруженных отгрузок (${upTo})${dz}`;
  if (r.to > last) return `период частично за пределами данных (${upTo})${dz}`;
  if (!cmp || last !== r.to) return null;
  const y = Number(last.slice(0, 4));
  const m = Number(last.slice(5, 7));
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  if (Number(salesDateTo.slice(8, 10)) >= days) return null;
  return `${monthLabel(r.to)} неполный (${upTo}), а период сравнения — полные месяцы: Δ занижена`;
}

/** Диапазон пресета. «Весь период» — от первого до последнего месяца витрины (вкл. будущие
 *  месяцы заказов); витрина пуста — null (пресет недоступен, а не «текущий месяц» молча). */
export function presetRange(
  id: Exclude<PresetId, "custom">,
  current: string,
  bounds?: { month_min: string | null; month_max: string | null } | null,
): MonthRange | null {
  const q0 = ofIdx(Math.floor(idx(current) / 3) * 3);
  switch (id) {
    case "tm":
      return { from: current, to: current };
    case "pm": {
      const p = addMonths(current, -1);
      return { from: p, to: p };
    }
    case "tq":
      return { from: q0, to: current };
    case "pq":
      return { from: addMonths(q0, -3), to: addMonths(q0, -1) };
    case "ytd":
      return { from: `${current.slice(0, 4)}-01`, to: current };
    case "all":
      if (!bounds?.month_min || !bounds.month_max) return null;
      return { from: bounds.month_min, to: bounds.month_max };
  }
}

/** Карточка drill-down в адресе: `m:<месяц>:<вид>`, `b:<покупатель>`, `p:<номенклатура>:<характеристика>`
 *  (пустая характеристика — строки без неё), `p:<номенклатура>` — товар по всем характеристикам
 *  (узел «Товар» дерева без характеристики в пути), `u:` — строки без себестоимости, `n:` — строки
 *  без ставки склада (отдельный список: закупка у них посчитана). */
export type Card =
  | { type: "month"; month: string; kind: Kind }
  | { type: "buyer"; ref: string }
  | { type: "product"; nomenclature: string; characteristic: string | null }
  | { type: "order"; ref: string }
  | { type: "portfolio"; kind: OpenKind }
  | { type: "unpriced" }
  | { type: "norate" }
  | { type: "node"; steps: NodeStep[] };

/** Виды портфеля заказов (открытые на дату снимка). */
export type OpenKind = "planned" | "overdue";

/** Шаг пути узла дерева: измерение, ключ (null — «без заказа» / «без характеристики»), подпись. */
export interface NodeStep {
  dim: TreeDim;
  key: string | null;
  label: string;
}

export function encodeCard(c: Card): string {
  switch (c.type) {
    case "month":
      return `m:${c.month}:${c.kind}`;
    case "buyer":
      return `b:${c.ref}`;
    case "product":
      return c.characteristic === null ? `p:${c.nomenclature}` : `p:${c.nomenclature}:${c.characteristic}`;
    case "order":
      return `o:${c.ref}`;
    case "portfolio":
      return `op:${c.kind}`;
    case "unpriced":
      return "u:";
    case "norate":
      return "n:";
    case "node":
      return `t:${JSON.stringify(c.steps.map((x) => [x.dim, x.key, x.label]))}`;
  }
}

/** `t:<JSON [[dim, key, label], …]>` → шаги узла; что-то не так — null (адрес покажет ошибку). */
function decodeSteps(tail: string): NodeStep[] | null {
  let raw: unknown;
  try {
    raw = JSON.parse(tail);
  } catch {
    return null;
  }
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const steps: NodeStep[] = [];
  for (const x of raw) {
    if (!Array.isArray(x) || x.length !== 3) return null;
    const [dim, key, label] = x as unknown[];
    if (!TREE_DIMS.some((d) => d.code === dim) || typeof label !== "string") return null;
    if (key !== null && (typeof key !== "string" || key === "")) return null;
    if (key === null && dim !== "characteristic" && dim !== "order") return null;
    if (dim === "month" && !isMonth(key as string)) return null;
    if (steps.some((st) => st.dim === dim)) return null;
    steps.push({ dim: dim as TreeDim, key: key as string | null, label });
  }
  return steps;
}

export function decodeCard(s: string | null): Card | null {
  if (!s) return null;
  const [t, ...rest] = s.split(":");
  const tail = rest.join(":");
  if (t === "m") {
    const [month, kind] = rest;
    if (isMonth(month) && (kind === "shipped" || kind === "planned" || kind === "overdue")) {
      return { type: "month", month, kind };
    }
    return null;
  }
  if (t === "b" && tail) return { type: "buyer", ref: tail };
  if (t === "o" && tail) return { type: "order", ref: tail };
  if (t === "op" && (tail === "planned" || tail === "overdue")) return { type: "portfolio", kind: tail };
  if (t === "p" && rest.length === 2 && rest[0]) {
    return { type: "product", nomenclature: rest[0], characteristic: rest[1] ?? "" };
  }
  if (t === "p" && rest.length === 1 && rest[0]) {
    return { type: "product", nomenclature: rest[0], characteristic: null };
  }
  if (t === "t") {
    const steps = decodeSteps(tail);
    return steps ? { type: "node", steps } : null;
  }
  if (t === "u") return { type: "unpriced" };
  if (t === "n") return { type: "norate" };
  return null;
}

/** Карточка сущности (покупатель, товар, заказ, узел дерева) → путь узла для `/node` и `/lines`;
 *  списки (месяц, портфель, «без цены») — null. Подписи шагов из адреса `b:` / `p:` / `o:` пусты:
 *  имя приходит с узлом. `p:<ном>:` (пустая характеристика) — строки без неё (ключ null). */
export function entitySteps(c: Card): NodeStep[] | null {
  switch (c.type) {
    case "buyer":
      return [{ dim: "buyer", key: c.ref, label: "" }];
    case "order":
      return [{ dim: "order", key: c.ref, label: "" }];
    case "product":
      return c.characteristic === null
        ? [{ dim: "product", key: c.nomenclature, label: "" }]
        : [
            { dim: "product", key: c.nomenclature, label: "" },
            { dim: "characteristic", key: c.characteristic === "" ? null : c.characteristic, label: "" },
          ];
    case "node":
      return c.steps;
    default:
      return null;
  }
}

/** Фильтр `/lines` для карточки: у сущности — как строка дерева (область экрана: отгрузки и к
 *  отгрузке периода + все просроченные; в иерархии с «Месяцем» — `monthTree`: все виды за месяцы
 *  периода); у месяца — только этот месяц и вид; портфель вида — к отгрузке в периоде / все
 *  просроченные; «без цены» — область экрана, как значок покрытия. */
export function cardQuery(c: Card, r: MonthRange, monthTree = false): LinesQuery {
  const steps = entitySteps(c);
  if (steps) return nodeQuery(steps, r, monthTree);
  switch (c.type) {
    case "month":
      return { month_from: c.month, month_to: c.month, kind: c.kind };
    case "portfolio":
      return { month_from: r.from, month_to: r.to, kind: c.kind, portfolio: true };
    case "unpriced":
      return { month_from: r.from, month_to: r.to, unpriced: true, portfolio: true };
    case "norate":
      return { month_from: r.from, month_to: r.to, no_rate: true, portfolio: true };
    default:
      throw new Error(`карточка ${c.type}: нет фильтра строк`);
  }
}

/** Фильтр строк узла дерева — ВСЕ шаги пути: покупатель, товар, характеристика (под товаром — ref,
 *  без товара выше — имя), заказ, месяц (у всех видов; у заказа — месяц даты отгрузки). Область —
 *  как у строки дерева: отгрузки и к отгрузке периода + все просроченные; в иерархии с «Месяцем»
 *  (`monthTree`) — все виды за месяцы периода. null-ключ — ''. */
export function nodeQuery(steps: NodeStep[], r: MonthRange, monthTree = false): LinesQuery {
  const q: LinesQuery = monthTree ? { month_from: r.from, month_to: r.to } : { month_from: r.from, month_to: r.to, portfolio: true };
  steps.forEach((st, i) => {
    const key = st.key ?? "";
    switch (st.dim) {
      case "buyer":
        q.buyer = key;
        break;
      case "product":
        q.nomenclature = key;
        break;
      case "characteristic":
        if (steps.slice(0, i).some((x) => x.dim === "product")) q.characteristic = key;
        else q.characteristic_name = key;
        break;
      case "order":
        q.order = key;
        break;
      case "month":
        q.month = key;
        break;
    }
  });
  return q;
}

/** Вкладки: дерево (вместо прежних «Покупатели» / «Товары») и «По месяцам». */
export type Tab = "tree" | "months";
export const TABS: { id: Tab; label: string }[] = [
  { id: "tree", label: "Дерево" },
  { id: "months", label: "По месяцам" },
];
export const DEFAULT_TAB: Tab = "tree";

// ─────────────── дерево ───────────────
/** Измерения дерева (коды — белый список бэкенда `wholesale/tree.py`). */
export const TREE_DIMS: { code: TreeDim; label: string }[] = [
  { code: "buyer", label: "Покупатель" },
  { code: "product", label: "Товар" },
  { code: "characteristic", label: "Характеристика" },
  { code: "month", label: "Месяц" },
  { code: "order", label: "Заказ" },
];

/** Готовые виды; первый — умолчание. */
export const TREE_PRESETS: HierarchyPreset[] = [
  { id: "buyer_product", label: "Покупатель → Товар", levels: ["buyer", "product"] },
  { id: "buyer_order_product", label: "Покупатель → Заказ → Товар", levels: ["buyer", "order", "product"] },
  { id: "product_buyer", label: "Товар → Покупатель", levels: ["product", "buyer"] },
  { id: "month_buyer", label: "Месяц → Покупатель", levels: ["month", "buyer"] },
];

export const TREE_SORT_KEYS: readonly TreeSortKey[] = [
  "revenue",
  "quantity",
  "margin",
  "margin_pct",
  "name",
  "planned",
  "overdue",
];
export const DEFAULT_TREE_SORT: TreeSortKey = "revenue";

export interface TreeSort {
  key: TreeSortKey;
  dir: TreeSortDir;
}

/** Разрезы адреса, которых нет в белом списке, и повторы — дерево по ним не строим (ошибка с
 *  кнопкой сброса, а не тихое исправление). */
export function badLevels(levels: string[]): string[] {
  const bad = levels.filter((c) => !TREE_DIMS.some((d) => d.code === c));
  const dup = levels.filter((c, i) => levels.indexOf(c) !== i);
  return [...new Set([...bad, ...dup])];
}

/** Направление столбца по умолчанию: имя — по алфавиту, числа — по убыванию. */
export function defaultTreeDir(key: TreeSortKey): TreeSortDir {
  return key === "name" ? "asc" : "desc";
}

/** Клик по заголовку: тот же столбец — обратное направление; новый — его умолчание. */
export function nextTreeSort(cur: TreeSort, key: TreeSortKey): TreeSort {
  if (cur.key === key) return { key, dir: cur.dir === "desc" ? "asc" : "desc" };
  return { key, dir: defaultTreeDir(key) };
}

/** Сортировка → адрес: `tsort` (выручка — без параметра), `tdir` (умолчание столбца — без
 *  параметра). */
export function treeSortPatch(s: TreeSort): Record<string, string | null> {
  return {
    tsort: s.key === DEFAULT_TREE_SORT ? null : s.key,
    tdir: s.dir === defaultTreeDir(s.key) ? null : s.dir,
  };
}

/** Карточка узла дерева — строки с фильтром по всему пути (узел и все его предки). */
export function nodeCard(path: NodeStep[], node: NodeStep): Card {
  return { type: "node", steps: [...path, node] };
}

export interface PageState {
  preset: PresetId;
  /** custom — из адреса; иначе считается от пресета (у «Весь период» — после /meta) */
  custom: MonthRange | null;
  /** сравнение с прошлым периодом (адрес `cmp=off` — без него) */
  cmp: CmpMode;
  tab: Tab;
  /** разрезы дерева (с выключенными глазиком) и id пресета, если совпали с ним */
  hierarchy: Hierarchy;
  treePreset: string | null;
  treeSort: TreeSort;
  card: Card | null;
  /** ошибки адреса — показываются, а не исправляются молча */
  errors: string[];
}

export function readState(p: URLSearchParams): PageState {
  const errors: string[] = [];
  const rawPreset = p.get("preset");
  let preset: PresetId = DEFAULT_PRESET;
  if (rawPreset !== null) {
    if (PRESETS.some((x) => x.id === rawPreset)) preset = rawPreset as PresetId;
    else errors.push(`неизвестный период «${rawPreset}» в адресе`);
  }
  let custom: MonthRange | null = null;
  if (preset === "custom") {
    const from = p.get("from");
    const to = p.get("to");
    if (isMonth(from) && isMonth(to) && from <= to) custom = { from, to };
    else errors.push(`свой период в адресе неверен: from=${from ?? "—"}, to=${to ?? "—"}`);
  }
  const rawCmp = p.get("cmp");
  let cmp: CmpMode = DEFAULT_CMP;
  if (rawCmp !== null) {
    if (CMP_MODES.some((x) => x.id === rawCmp)) cmp = rawCmp as CmpMode;
    else errors.push(`неизвестное сравнение «${rawCmp}» в адресе`);
  }
  const rawTab = p.get("tab");
  // прежние вкладки «Покупатели» / «Товары» (ссылки до v2) — дерево с тем же первым уровнем
  const legacy = rawTab === "buyers" || rawTab === "products";
  let tab: Tab = DEFAULT_TAB;
  if (TABS.some((x) => x.id === rawTab)) tab = rawTab as Tab;
  else if (rawTab !== null && !legacy) errors.push(`неизвестная вкладка «${rawTab}» в адресе`);
  const hp = new URLSearchParams(p);
  const tp = p.get("tp");
  if (tp !== null && !TREE_PRESETS.some((x) => x.id === tp)) {
    errors.push(`неизвестный вид дерева «${tp}» в адресе`);
  }
  if (rawTab === "products" && !hp.get("lv") && !hp.get("tp")) hp.set("tp", "product_buyer");
  const h = readHierarchy(hp, TREE_PRESETS);
  const rawSort = p.get("tsort");
  let sortKey: TreeSortKey = DEFAULT_TREE_SORT;
  if (rawSort !== null) {
    if ((TREE_SORT_KEYS as readonly string[]).includes(rawSort)) sortKey = rawSort as TreeSortKey;
    else errors.push(`неизвестная сортировка «${rawSort}» в адресе`);
  }
  const rawDir = p.get("tdir");
  let dir = defaultTreeDir(sortKey);
  if (rawDir === "asc" || rawDir === "desc") dir = rawDir;
  else if (rawDir !== null) errors.push(`неизвестное направление сортировки «${rawDir}» в адресе`);
  const rawCard = p.get("card");
  const card = decodeCard(rawCard);
  if (rawCard && !card) errors.push(`неизвестная карточка «${rawCard}» в адресе`);
  return {
    preset,
    custom,
    cmp,
    tab,
    hierarchy: { levels: h.levels, off: h.off },
    treePreset: h.preset,
    treeSort: { key: sortKey, dir },
    card,
    errors,
  };
}

// ─────────────── числа ───────────────
/** Расход в знаке витрины (−) → положительное число для колонки «Себестоимость» / «Склад». */
export function expense(v: number | null): number | null {
  return v === null ? null : -v;
}

export function qty(n: number): string {
  // штуки в 1С бывают дробными (базовые единицы)
  return Number.isInteger(n) ? int(n) : n.toFixed(3).replace(".", ",").replace(/0+$/, "");
}

export const rubOrDash = (v: number | null | undefined): string =>
  v === null || v === undefined ? "—" : rub(v);

/** Рубли компактно, как в «Аналитике МП»: «1,2 млн ₽», «45 тыс ₽», «480 ₽». */
export function rubCompact(n: number): string {
  return `${compact(n)}${NBSP}₽`;
}

/** Компактный вид ячейки (тыс/млн): точная сумма — в подсказке, перед причиной «*» / «—».
 *  `shown` — число, которое ячейка показывает (у расходов — без минуса). */
export function compactCell<T extends MoneyCell>(c: T, shown: number | null | undefined): T {
  if (shown === null || shown === undefined || c.text === "—") return c;
  return { ...c, text: rubCompact(shown), title: c.title ? `${c.text} — ${c.title}` : c.text };
}

/** Ячейка денег агрегата: «—» с причиной, если суммы нет; пометка, если сумма частичная. */
export interface MoneyCell {
  text: string;
  /** часть строк в сумму не вошла (нет цены / нет суммы заказа) */
  partial: boolean;
  title: string | null;
}

function missingText(b: Bucket, what: string): string {
  return `${what}: ${int(b.rows)} ${plural(b.rows, "строка", "строки", "строк")}, ${qty(b.quantity)} шт`;
}

/** Отгрузка со штуками и суммой 0 (`zero_amount`, контракт v1.4): нейтральная пометка — выручка 0
 *  по факту (не «нет цены»), штуки считаются, маржа — только расход (себестоимость и склад). */
export const ZERO_AMOUNT_LABEL = "сумма 0";
const ZERO_AMOUNT_EFFECT = "штуки в отгрузках, выручка 0, маржа — только расход";

export const UNKNOWN_SUM = "сумма неизвестна";

/** Выручка бакета: сумма известна — рубли (если часть строк без суммы — с их числом); ни у одной
 *  строки нет суммы (no_order_line / unknown_unit) — «сумма неизвестна», а не «0 ₽». */
export function bucketMoney(b: Bucket): string {
  if (b.revenue_gross === null) return UNKNOWN_SUM;
  if (b.revenue_unknown_rows > 0) {
    return `${rub(b.revenue_gross)} + ${int(b.revenue_unknown_rows)} ${plural(b.revenue_unknown_rows, "строка", "строки", "строк")} без суммы`;
  }
  return rub(b.revenue_gross);
}

/** Выручка агрегата. Частичная («*»), если в сумму не вошли строки без суммы заказа ИЛИ в ней есть
 *  строки с суммой заказа 0 (zero_price: оценены нулём, это не настоящая выручка). */
export function revenueCell(a: Agg | null): MoneyCell {
  if (!a) return { text: "—", partial: false, title: null };
  const miss = a.revenue_missing.rows > 0;
  const zero = a.revenue_status.zero_price;
  const zeroRows = zero.rows > 0;
  const notes: string[] = [];
  if (miss) notes.push(`не вошло: ${missingText(a.revenue_missing, "нет суммы заказа")}`);
  if (zeroRows) notes.push(`в сумме по нулю: ${missingText(zero, "сумма заказа 0")}`);
  let title = notes.length > 0 ? `часть без суммы — ${notes.join("; ")}` : null;
  // отгрузка за 0 ₽ (v1.4) — выручка 0 по факту, итог не частичный; только пояснение
  const za = a.revenue_status.zero_amount;
  if (za.rows > 0) {
    const note = `${missingText(za, `в т.ч. ${ZERO_AMOUNT_LABEL}`)} — ${ZERO_AMOUNT_EFFECT}`;
    title = title ? `${title}; ${note}` : note;
  }
  if (a.revenue_gross === null) {
    return { text: "—", partial: miss || zeroRows, title: title ?? missingText(a.revenue_missing, "нет суммы заказа") };
  }
  return { text: rub(a.revenue_gross), partial: miss || zeroRows, title };
}

/** Выручка без НДС — та же пометка частичности, что у выручки с НДС (те же строки). */
export function netRevenueCell(a: Agg | null): MoneyCell {
  const g = revenueCell(a);
  if (!a) return g;
  return { text: rubOrDash(a.revenue_net), partial: g.partial, title: g.title };
}

/** Колонка себестоимости: причина «—» и частичности — по статусу, который её обнуляет. Закупка
 *  (`goods`) пуста при `no_price`; склад (`wh`) — при `no_warehouse_rate`. */
export type CostColumn = "goods" | "wh";
const COST_REASON: Record<CostColumn, { none: string; part: string }> = {
  goods: { none: "нет себестоимости", part: "часть без себестоимости" },
  wh: { none: "нет ставки склада", part: "часть без ставки склада" },
};

export function costCell(v: number | null, missing: Bucket, a: Agg | null, col: CostColumn): MoneyCell {
  if (!a) return { text: "—", partial: false, title: null };
  const miss = missing.rows > 0;
  const reason = COST_REASON[col];
  if (v === null) return { text: "—", partial: miss, title: missingText(missing, reason.none) };
  return {
    text: rub(-v),
    partial: miss,
    title: miss ? `${reason.part} — ${missingText(missing, "не вошло")}` : null,
  };
}

/** Маржа агрегата: Σ по оценённым строкам; часть без цены — пометка, все без цены — «—». */
export function marginCell(a: Agg | null): MoneyCell & { pct: string } {
  if (!a) return { text: "—", partial: false, title: null, pct: "—" };
  if (a.net_margin === null) {
    return {
      text: "—",
      partial: a.unvalued.rows > 0,
      title: `маржи нет: ${unvaluedReasons(a).join("; ") || "нет оценённых строк"}`,
      pct: "—",
    };
  }
  return {
    text: rub(a.net_margin),
    partial: !a.complete,
    title: a.complete
      ? null
      : `часть без цены — маржа по ${rub(a.margin_base_revenue)} выручки; не оценено: ${unvaluedReasons(a).join("; ")}`,
    pct: pct(a.margin_pct),
  };
}

const PRICE_LABEL: Record<"no_price" | "no_warehouse_rate", string> = {
  no_price: "нет себестоимости",
  no_warehouse_rate: "нет ставки склада",
};
const REVENUE_LABEL: Record<"zero_price" | "no_order_line" | "unknown_unit", string> = {
  zero_price: "сумма заказа 0",
  no_order_line: "нет строки заказа",
  unknown_unit: "единица заказа не распознана",
};

function bucketText(b: Bucket, label: string): string {
  return `${label}: ${qty(b.quantity)} шт, ${bucketMoney(b)}`;
}

/** Почему строки не оценены — по статусам контракта (строка может иметь две причины сразу). */
export function unvaluedReasons(a: Agg): string[] {
  const out: string[] = [];
  for (const k of ["no_price", "no_warehouse_rate"] as const) {
    if (a.price_status[k].rows > 0) out.push(bucketText(a.price_status[k], PRICE_LABEL[k]));
  }
  for (const k of ["zero_price", "no_order_line", "unknown_unit"] as const) {
    if (a.revenue_status[k].rows > 0) out.push(bucketText(a.revenue_status[k], REVENUE_LABEL[k]));
  }
  return out;
}

// ─────────────── портфель: к отгрузке вне периода ───────────────
/** Справка «К отгрузке» с датой отгрузки вне периода (в KPI, дерево и карточки не входит, решение
 *  владельца 23.10.2026): «позже: 2 400 ₽ · 30 шт — окт 2026, ноя 2026». Часть строк без суммы
 *  заказа — «*» и причина в подсказке; суммы нет ни у одной — «сумма неизвестна» (не «0 ₽»).
 *  `before` — раньше начала периода: бывает, когда период в будущем (такие заказы не просрочены). */
export function outsideNote(side: "after" | "before", a: OutsideAgg): { text: string; title: string } {
  const rev = revenueCell(a);
  const money = rev.text === "—" ? "сумма неизвестна" : `${rev.text}${rev.partial ? "*" : ""}`;
  const head = side === "after" ? "позже" : "раньше периода";
  const what =
    side === "after"
      ? "к отгрузке с датой отгрузки после конца периода"
      : "к отгрузке с датой отгрузки до начала периода (период в будущем — заказы ещё не просрочены)";
  return {
    text: `${head}: ${money} · ${qty(a.quantity)} шт — ${a.months.map(monthLabel).join(", ")}`,
    title: `${what}; в KPI, дерево и карточки не входит${rev.title ? `; ${rev.title}` : ""}`,
  };
}

// ─────────────── плашка покрытия ───────────────
export interface CoverageLine {
  kind: Kind;
  status: string;
  label: string;
  bucket: Bucket;
}

export interface Coverage {
  /** warn — есть строки без цены / суммы; ok — всё оценено; empty — строк в периоде нет */
  tone: "warn" | "ok" | "empty";
  lines: CoverageLine[];
  /** есть строки без себестоимости (`no_price`) — показать список товаров */
  hasUnpriced: boolean;
  /** есть строки без ставки склада (`no_warehouse_rate`) — отдельный список */
  hasNoRate: boolean;
  /** строк без маржи (`unvalued`) по всем видам — каждая один раз, хоть причин у неё и две */
  unvaluedRows: number;
  /** пояснения, не пробелы (на `tone` не влияют): отгрузки за 0 ₽ (`zero_amount`, v1.4) */
  notes: CoverageLine[];
}

/** Область экрана по видам: отгрузки и к отгрузке периода + все просроченные на дату снимка — то,
 *  что показывают KPI и дерево; по ней — покрытие ценой и списки «без цены». */
export function screenScope(s: Pick<WholesaleSummary, "totals" | "open_orders">): PerKind {
  return { shipped: s.totals.shipped, planned: s.open_orders.planned, overdue: s.open_orders.overdue };
}

/** Подпись вида в области экрана (покрытие ценой): к отгрузке — за период, просрочено — все. */
export const SCOPE_LABEL: Record<Kind, string> = {
  shipped: "Отгружено за период",
  planned: "К отгрузке в периоде",
  overdue: "Просрочено (все на дату снимка)",
};

export function coverage(totals: Record<Kind, Agg | null>): Coverage {
  const lines: CoverageLine[] = [];
  const notes: CoverageLine[] = [];
  let any = false;
  let unvaluedRows = 0;
  for (const kind of ["shipped", "planned", "overdue"] as const) {
    const a = totals[kind];
    if (!a) continue;
    any = true;
    unvaluedRows += a.unvalued.rows;
    for (const k of ["no_price", "no_warehouse_rate"] as const) {
      const b = a.price_status[k];
      if (b.rows > 0) lines.push({ kind, status: k, label: PRICE_LABEL[k], bucket: b });
    }
    for (const k of ["zero_price", "no_order_line", "unknown_unit"] as const) {
      const b = a.revenue_status[k];
      if (b.rows > 0) lines.push({ kind, status: k, label: REVENUE_LABEL[k], bucket: b });
    }
    const za = a.revenue_status.zero_amount;
    if (za.rows > 0) notes.push({ kind, status: "zero_amount", label: ZERO_AMOUNT_LABEL, bucket: za });
  }
  if (!any) return { tone: "empty", lines, hasUnpriced: false, hasNoRate: false, unvaluedRows, notes };
  return {
    tone: lines.length > 0 ? "warn" : "ok",
    lines,
    unvaluedRows,
    notes,
    hasUnpriced: lines.some((l) => l.status === "no_price"),
    hasNoRate: lines.some((l) => l.status === "no_warehouse_rate"),
  };
}

/** Что у строк со статусом посчитано, а что нет (по контракту: маржа — только при ok / ok). */
const STATUS_EFFECT: Record<string, string> = {
  no_price: "себестоимость и маржа не посчитаны",
  no_warehouse_rate: "себестоимость посчитана, склад и маржа — нет",
  zero_price: "себестоимость и склад посчитаны, маржи нет",
  no_order_line: "себестоимость и склад посчитаны, суммы и маржи нет",
  unknown_unit: "себестоимость и склад посчитаны, суммы и маржи нет",
  zero_amount: ZERO_AMOUNT_EFFECT,
};

export function coverageLineText(l: CoverageLine): string {
  const sum = l.bucket.revenue_gross === null ? UNKNOWN_SUM : `${bucketMoney(l.bucket)} выручки`;
  const effect = STATUS_EFFECT[l.status];
  return `${SCOPE_LABEL[l.kind]} — ${l.label}: ${int(l.bucket.rows)} ${plural(l.bucket.rows, "строка", "строки", "строк")}, ${qty(l.bucket.quantity)} шт, ${sum}${effect ? ` (${effect})` : ""}`;
}

/** Строка-значок покрытия: сколько строк области экрана без цены / ставки / суммы заказа. */
export function coverageChipText(c: Coverage): string {
  if (c.tone === "ok") return "Цена и сумма заказа есть у всех строк";
  const rows = c.unvaluedRows;
  return `Без цены, ставки склада или суммы заказа: ${int(rows)} ${plural(rows, "строка", "строки", "строк")} — маржа по ним не посчитана (не нулём)`;
}

// ─────────────── флаги ───────────────
/** Пометки периода: корректировки без исходной накладной. Покупатель без ИНН не помечается
 *  (решение владельца 22.10.2026: иностранные покупатели — норма; в витрине статус остаётся). */
export function flags(totals: Record<Kind, Agg | null>): string[] {
  const out: string[] = [];
  const unres = totals.shipped?.base_unresolved;
  if (unres && unres.rows > 0) {
    out.push(
      `Корректировка без найденной исходной накладной — учтена месяцем самой корректировки: ${int(unres.rows)} ${plural(unres.rows, "строка", "строки", "строк")}, ${bucketMoney(unres)}`,
    );
  }
  return out;
}

// ─────────────── комиссионер ───────────────
/** Подсказка у покупателя-комиссионера (контракт v1.4, решения владельца 23.10.2026). */
export const COMMISSIONER_HINT =
  "Комиссионер: выручка по передаче на комиссию, по цене передачи; отчёты комиссионера не считаются";

/** Покупатель узла — комиссионер: среди его строк есть передача на комиссию / возврат от
 *  комиссионера (по `method`, бакет `commission`), в периоде, портфеле или периоде сравнения.
 *  Имя контрагента не используется. */
export function isCommissioner(n: Pick<TreeNode, "shipped" | "planned" | "overdue" | "shipped_cmp">): boolean {
  return [n.shipped, n.planned, n.overdue, n.shipped_cmp].some((a) => (a?.commission.rows ?? 0) > 0);
}

// ─────────────── что не вошло ───────────────
const SHIPPED_REASON: Record<string, string> = {
  own_buyer: "свои юрлица",
  other_category: "номенклатура другой категории",
  unknown_buyer: "покупатель не найден в справочнике",
  unknown_nomenclature: "номенклатура не найдена в справочнике",
  // v1.4, решения владельца 23.10.2026: комиссионеру выручка — по передаче на комиссию
  commission_report: "отчёт комиссионера (выручка по передаче на комиссию)",
  commission_buyout: "выкуп комиссионером (уже учтён при передаче)",
  commission_write_off: "списание у комиссионера (не учитывается)",
};
const ORDERS_REASON: Record<string, string> = {
  closed_remainder: "остаток закрытого заказа (недогруз)",
  unknown_state: "состояние заказа не классифицировано",
  own_buyer: "свои юрлица",
  other_category: "номенклатура другой категории",
  not_posted: "заказ не проведён",
  no_shipment_date: "нет даты отгрузки",
  unknown_order: "заказ не найден",
  // v1.3, решение владельца 22.10.2026: остатки заказов с суммой 0 витрина не включает
  zero_amount_orders: "Заказы с суммой 0 — не учитываются",
};

export interface ExcludedLine {
  where: "shipped" | "orders";
  reason: string;
  label: string;
  /** требует внимания: новая причина / неклассифицированное состояние */
  alert: boolean;
  text: string;
}

function reasonText(r: ExcludedReason): string {
  const parts = [`${int(r.rows)} ${plural(r.rows, "строка", "строки", "строк")}`];
  // число заказов — у причин остатков заказов с v1.3 (в старой сборке его нет — не пишем)
  if (r.orders !== undefined && r.orders !== null) {
    parts.push(`${int(r.orders)} ${plural(r.orders, "заказ", "заказа", "заказов")}`);
  }
  parts.push(r.quantity === null ? "шт — нет данных" : `${qty(r.quantity)} шт`);
  parts.push(r.revenue_gross === null ? "сумма — нет данных" : rub(r.revenue_gross));
  return parts.join(", ");
}

export function excludedLines(st: WholesaleState): ExcludedLine[] {
  const map = (where: "shipped" | "orders", labels: Record<string, string>) => (r: ExcludedReason) => ({
    where,
    reason: r.reason,
    label: r.known ? (labels[r.reason] ?? r.reason) : `новая причина «${r.reason}»`,
    alert: !r.known || r.reason === "unknown_state",
    text: reasonText(r),
  });
  return [
    ...st.shipped_excluded.map(map("shipped", SHIPPED_REASON)),
    ...st.orders_excluded.map(map("orders", ORDERS_REASON)),
  ];
}

/** Строка свежести: «Продажи 1С по 21.10.2026, остаток заказов на 22.10.2026». */
export function freshnessText(st: WholesaleState): string {
  return `Отгрузки 1С по ${fmtDate(st.sales_date_to)}, остаток заказов на ${fmtDate(st.orders_as_of)}`;
}

export function periodLabel(r: MonthRange): string {
  return r.from === r.to ? monthLabel(r.from) : `${monthLabel(r.from)} — ${monthLabel(r.to)}`;
}

/** Документ строки по-человечески. */
export const DOC_LABEL: Record<string, string> = {
  "Document_РасходнаяНакладная": "Расходная накладная",
  "Document_КорректировкаРеализации": "Корректировка реализации",
  "Document_ОтчетКомиссионера": "Отчёт комиссионера",
  "Document_ПриходнаяНакладная": "Приходная накладная",
  "Document_ЗаказПокупателя": "Заказ покупателя",
};

export function docLabel(t: string): string {
  return DOC_LABEL[t] ?? t.replace(/^Document_/, "");
}

/** Название товара: артикул · наименование 1С (нет — код, нет — ключ). */
export function productTitle(
  p: Pick<ProductRow, "article_1c" | "name_1c" | "code_1c" | "nomenclature_ref">,
): string {
  return [p.article_1c, p.name_1c].filter(Boolean).join(" · ") || p.code_1c || p.nomenclature_ref;
}

/** Старая ссылка с вкладкой «Покупатели» / «Товары» (до v2) → адрес дерева (один раз при загрузке:
 *  иначе устаревший `tab` продолжал бы перебивать выбранный вид). Не старая — null. */
export function legacyTabPatch(p: URLSearchParams): Record<string, string | null> | null {
  const tab = p.get("tab");
  if (tab !== "buyers" && tab !== "products") return null;
  const patch: Record<string, string | null> = { tab: null };
  if (tab === "products" && !p.get("lv") && !p.get("tp")) patch.tp = "product_buyer";
  return patch;
}

/** Строка фильтров карточки узла: «Покупатель: …» · «Товар: …» · … */
export function stepsText(steps: NodeStep[]): string {
  return steps
    .map((x) => `${TREE_DIMS.find((d) => d.code === x.dim)?.label ?? x.dim}: ${x.dim === "month" && x.key ? monthLabel(x.key) : x.label}`)
    .join(" · ");
}
